import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';

export default function ContactPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5no2bcl56fkiit4nr80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: ''
        });
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead 
        title="Contact Technelle | AI Marketing Waukesha, WI"
        description="Get in touch with Technelle, Waukesha Wisconsin's AI-powered marketing agency. Call 414-530-9114 or email melissa@technelle.ai. Book your free 30-minute Wisconsin business strategy consultation today."
        keywords="contact marketing agency Waukesha, free strategy call Wisconsin, marketing consultation Waukesha WI, AI marketing consultation Wisconsin, business consulting Waukesha"
        canonical="https://technelle.ai/contact"
        ogTitle="Contact Technelle | AI Marketing Waukesha, WI"
        ogDescription="Ready to transform your Wisconsin business? Contact us at 414-530-9114 or melissa@technelle.ai. Based in Waukesha, WI. Free 30-minute strategy consultation."
        ogUrl="https://technelle.ai/contact"
        breadcrumbs={[
          { name: 'Home', url: 'https://technelle.ai/' },
          { name: 'Contact', url: 'https://technelle.ai/contact' }
        ]}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="container mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle - AI Marketing & Consulting" className="h-14" />
          </Link>
          <div className="hidden lg:flex items-center gap-10">
            <Link to="/" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Home</Link>
            <Link to="/about" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">About</Link>
            <Link to="/services" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Services</Link>
            <Link to="/industries" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Industries</Link>
            <Link to="/contact" className="text-[15px] text-[#2196F3] transition-colors font-medium">Contact</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link 
              to="/contact"
              className="hidden lg:block bg-[#2196F3] text-white px-6 sm:px-8 py-3 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors whitespace-nowrap cursor-pointer min-w-[44px] min-h-[44px]"
            >
              Get In Touch
            </Link>
            <button
              onClick={() => setMobileNavOpen(true)}
              className="lg:hidden w-11 h-11 flex items-center justify-center text-[#1a1a1a] cursor-pointer"
              aria-label="Open mobile menu"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Nav Drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-black/60 z-[200] lg:hidden" onClick={() => setMobileNavOpen(false)}>
          <div className="absolute top-0 right-0 w-72 h-full bg-white shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 h-20 border-b border-gray-100">
              <span className="text-[#1a1a1a] font-semibold text-[16px]">Menu</span>
              <button onClick={() => setMobileNavOpen(false)} className="w-10 h-10 flex items-center justify-center text-[#1a1a1a] cursor-pointer" aria-label="Close menu">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <nav className="flex flex-col px-6 py-8 gap-6">
              <Link to="/" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#2196F3] text-[16px] font-medium transition-colors">Home</Link>
              <Link to="/about" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#2196F3] text-[16px] font-medium transition-colors">About</Link>
              <Link to="/services" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#2196F3] text-[16px] font-medium transition-colors">Services</Link>
              <Link to="/contact" onClick={() => setMobileNavOpen(false)} className="text-[#2196F3] text-[16px] font-medium">Contact</Link>
            </nav>
            <div className="px-6 mt-auto pb-8">
              <Link
                to="/contact"
                onClick={() => setMobileNavOpen(false)}
                className="block w-full text-center bg-[#2196F3] text-white py-4 rounded-md text-[15px] font-semibold hover:bg-[#1976D2] transition-colors"
              >
                Get In Touch
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-50 to-white overflow-hidden">
        <div className="absolute top-0 left-0 w-1/3 h-full opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/c933e872080b04cecb92d9bad83760e4.jpg"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 w-full">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[13px] uppercase tracking-widest font-semibold text-[#2196F3] mb-4">Waukesha, Wisconsin</p>
            <h1 className="text-[32px] sm:text-[44px] lg:text-[60px] leading-[1.1] font-bold text-[#1a1a1a] mb-6 font-serif">
              Contact Technelle — AI Marketing &amp; Consulting for Wisconsin Businesses
            </h1>
            <p className="text-[18px] text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Ready to transform your marketing with AI? Reach out and let's discuss how Technelle can drive measurable growth for your Wisconsin business.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="bg-white py-24">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div>
              <h2 className="text-[36px] font-serif font-bold text-[#1a1a1a] mb-6">Send Us a Message</h2>
              <p className="text-[16px] text-gray-600 mb-8 leading-relaxed">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              <form className="space-y-6" onSubmit={handleFormSubmit} data-readdy-form id="contact-page-form">
                <div>
                  <label htmlFor="name" className="block text-[14px] font-medium text-[#1a1a1a] mb-2">Full Name *</label>
                  <input 
                    type="text" 
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-4 py-3 text-[15px] focus:outline-none focus:border-[#2196F3] transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-[14px] font-medium text-[#1a1a1a] mb-2">Email Address *</label>
                  <input 
                    type="email" 
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-4 py-3 text-[15px] focus:outline-none focus:border-[#2196F3] transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-[14px] font-medium text-[#1a1a1a] mb-2">Company Name *</label>
                  <input 
                    type="text" 
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-4 py-3 text-[15px] focus:outline-none focus:border-[#2196F3] transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-[14px] font-medium text-[#1a1a1a] mb-2">Phone Number</label>
                  <input 
                    type="tel" 
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-4 py-3 text-[15px] focus:outline-none focus:border-[#2196F3] transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-[14px] font-medium text-[#1a1a1a] mb-2">Message *</label>
                  <textarea 
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full border border-gray-300 rounded-md px-4 py-3 text-[15px] focus:outline-none focus:border-[#2196F3] transition-colors resize-none"
                  ></textarea>
                  <div className="text-[13px] text-gray-500 mt-1">{formData.message.length}/500 characters</div>
                </div>

                <button 
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-[#2196F3] text-white py-4 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  {formStatus === 'submitting' ? 'Sending...' : formStatus === 'success' ? 'Message Sent!' : 'Send Message'}
                </button>

                {formStatus === 'success' && (
                  <div className="text-green-600 text-[14px] text-center" role="status">
                    Thank you! We'll get back to you within 24 hours.
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="text-red-600 text-[14px] text-center" role="status">
                    Something went wrong. Please try again or contact us directly.
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-[36px] font-serif font-bold text-[#1a1a1a] mb-6">Get In Touch</h2>
              
              <div className="space-y-8 mb-12">
                <div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#2196F3] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-white text-xl" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h3 className="text-[18px] font-semibold text-[#1a1a1a] mb-2">Phone</h3>
                      <a href="tel:4145309114" className="text-[16px] text-gray-700 hover:text-[#2196F3] transition-colors">
                        414-530-9114
                      </a>
                      <p className="text-[14px] text-gray-500 mt-1">Mon-Fri, 9am-6pm CST</p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#2196F3] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-white text-xl" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h3 className="text-[18px] font-semibold text-[#1a1a1a] mb-2">Email</h3>
                      <a href="mailto:melissa@technelle.ai" className="text-[16px] text-gray-700 hover:text-[#2196F3] transition-colors">
                        melissa@technelle.ai
                      </a>
                      <p className="text-[14px] text-gray-500 mt-1">We'll respond within 24 hours</p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#2196F3] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-white text-xl" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h3 className="text-[18px] font-semibold text-[#1a1a1a] mb-2">Service Area</h3>
                      <p className="text-[16px] text-gray-700">Based in Waukesha, Wisconsin</p>
                      <p className="text-[14px] text-gray-500 mt-1">Serving businesses across Wisconsin and nationwide</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Time Info */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-[20px] font-bold text-[#1a1a1a] mb-3">What to Expect</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-time-line text-[#2196F3] text-xl mt-0.5" aria-hidden="true"></i>
                    <div>
                      <strong className="text-[15px] text-[#1a1a1a]">24-Hour Response</strong>
                      <p className="text-[14px] text-gray-600">We'll get back to you within one business day</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-calendar-check-line text-[#2196F3] text-xl mt-0.5" aria-hidden="true"></i>
                    <div>
                      <strong className="text-[15px] text-[#1a1a1a]">Free Strategy Call</strong>
                      <p className="text-[14px] text-gray-600">30-minute consultation to discuss your goals</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-file-list-line text-[#2196F3] text-xl mt-0.5" aria-hidden="true"></i>
                    <div>
                      <strong className="text-[15px] text-[#1a1a1a]">Custom Proposal</strong>
                      <p className="text-[14px] text-gray-600">Tailored strategy and pricing for your business</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d47180.64425184034!2d-88.26289324999999!3d43.0117073!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88051e9346a77dbb%3A0x6ea7c0d3d1f3e15e!2sWaukesha%2C%20WI!5e0!3m2!1sen!2sus!4v1234567890123"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Technelle Location - Waukesha, Wisconsin"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-24">
        <div className="container mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-[32px] sm:text-[40px] lg:text-[48px] font-serif font-bold text-[#1a1a1a] mb-6">
            Prefer to <span className="relative inline-block text-[#2196F3]">
              Talk Live
              <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 180 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M2 4C50 2 130 2 178 4" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>?
          </h2>
          <p className="text-[18px] text-gray-600 mb-8 max-w-2xl mx-auto">
            Book a free 30-minute strategy call to discuss your marketing goals and explore how we can help.
          </p>
          <a 
            href="tel:4145309114"
            className="inline-block bg-[#2196F3] text-white px-10 py-4 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors min-w-[44px] min-h-[44px]"
          >
            Call 414-530-9114
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle" className="h-12" width="48" height="48" />
              </div>
            </div>

            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Links</h5>
              <nav className="space-y-3">
                <Link to="/" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Home</Link>
                <Link to="/about" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">About</Link>
                <Link to="/services" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Services</Link>
                <Link to="/industries" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Industries</Link>
                <Link to="/contact" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Contact</Link>
              </nav>
            </div>

            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Contact</h5>
              <div className="space-y-3">
                <a href="tel:4145309114" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">414-530-9114</a>
                <a href="mailto:melissa@technelle.ai" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">melissa@technelle.ai</a>
              </div>
            </div>

            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Legal</h5>
              <nav className="space-y-3">
                <Link to="/privacy-policy" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Privacy Policy</Link>
                <Link to="/terms" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Terms</Link>
              </nav>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="text-[14px] text-gray-400">© 2025 Technelle. All rights reserved.</div>

            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
